import React from "react";
import styled from "@emotion/styled";

import { Radio } from "../Radio";
import { Card } from "../Card/Card";
import { Typography } from "../Typography";
import { CreditStatus } from "./CreditStatus";
import { TEST_ID_PREFIX } from "./constants";
import type { Contact, ContactCardProps } from "./types";
import { Row, UnderlinedButtonStyle } from "./shared";

const ContactOption = ({
  contact,
  contactType,
  customApprovedStatusText,
  customNotApprovedStatusText,
  customNotCheckedStatusText,
  isSelected,
  onSelect,
  onRemove,
  onEdit,
}: ContactOptionProps) => {
  const handleSelection = (contact: Contact) =>
    onSelect({ ...contact, primary: true });

  const isCreditChecked = [
    contact?.creditStatus?.isFailed,
    contact?.creditStatus?.isPassed,
  ].includes(true);

  const testPrefix = TEST_ID_PREFIX[contactType];

  return (
    <CardStyled
      isSelected={isSelected}
      onClick={() => handleSelection(contact)}
    >
      <Row>
        <Radio
          checked={isSelected}
          onChange={() => handleSelection(contact)}
          data-testid={`${testPrefix}-select-contact-${contact.contactId}`}
        />
        <Typography>{`${contact.customerFirstName} ${contact.customerLastName}`}</Typography>
        <RowWithGap>
          {isCreditChecked &&
            (contactType === "primary" || contactType === "secondary") && (
              <CreditStatus
                creditStatus={contact?.creditStatus}
                customApprovedStatusText={customApprovedStatusText}
                customNotApprovedStatusText={customNotApprovedStatusText}
                customNotCheckedStatusText={customNotCheckedStatusText}
              />
            )}
          {onRemove && (
            <UnderlinedButtonStyle
              size="xs"
              color="tertiary"
              onClick={() => onRemove?.(contact)}
            >
              Remove
            </UnderlinedButtonStyle>
          )}
          <UnderlinedButtonStyle
            size="xs"
            data-testid={`${testPrefix}-edit-contact-${contact.contactId}-underlined-button`}
            color="tertiary"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(contact);
            }}
          >
            Edit
          </UnderlinedButtonStyle>
        </RowWithGap>
      </Row>
    </CardStyled>
  );
};

interface ContactOptionProps
  extends Pick<
    ContactCardProps,
    | "contactType"
    | "customNotApprovedStatusText"
    | "customApprovedStatusText"
    | "customNotCheckedStatusText"
  > {
  onRemove?: (removedContact: Contact) => void;
  onSelect: (selectedContact: Contact) => void;
  onEdit?: (contact: Contact) => void;
  contact: Contact;
  isSelected: boolean;
}

const RowWithGap = styled(Row)`
  gap: 16px;
  margin-left: auto;
`;

const CardStyled = styled(Card)<{
  isSelected: boolean;
}>`
  border-color: ${(props) =>
    props.isSelected ? "var(--brand-hero-blue)" : undefined};

  background: ${(props) => (props.isSelected ? "var(--white)" : undefined)};

  :hover {
    cursor: pointer;
  }
`;

export type { ContactOptionProps };
export { ContactOption };
